import type { Customer } from '@/data/types';

// The customer's persisted last/next inspection meta is often stale: the legacy
// Excel migration carried over inspection dates that were never updated on the
// customer record even though the individual devices were serviced. Reminders,
// status and the detail view therefore derive these dates from the devices.
//
// The devices' own stored `nextInspection` is ALSO unreliable in the migrated
// data (many are older than the device's last service, or nonsensical). So the
// stored next date is trusted only when it is plausible — not earlier than the
// last service. Otherwise the next-due date is reconstructed from the last
// service plus the inspection interval (Swiss extinguisher maintenance cycle,
// default 3 years). ISO yyyy-mm-dd strings compare lexicographically.

const DEFAULT_INTERVAL_YEARS = 3;

export function addYears(iso: string, years: number): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  // Clamp 29 Feb to 28 to avoid an invalid date in a non-leap target year.
  const day = m === '02' && d === '29' ? '28' : d;
  return `${Number(y) + years}-${m}-${day}`;
}

// Effective next-due date for one device: trust the stored next when it is not
// before the last service, else reconstruct last + interval.
function deviceNextDue(
  last: string | undefined,
  storedNext: string | undefined,
  intervalYears: number
): string | undefined {
  if (storedNext && (!last || storedNext >= last)) return storedNext;
  if (last) return addYears(last, intervalYears);
  return storedNext;
}

/** Soonest upcoming service across all devices — the customer's effective next
 * inspection. Falls back to the stored customer value only when no device
 * carries a date. */
export function deriveNextInspection(
  c: Pick<Customer, 'extinguishers' | 'hoses' | 'nextInspection'>,
  intervalYears: number = DEFAULT_INTERVAL_YEARS
): string | undefined {
  const dues: string[] = [];
  for (const e of c.extinguishers ?? []) {
    const due = deviceNextDue(
      e.lastInspection,
      e.nextInspection,
      intervalYears
    );
    if (due) dues.push(due);
  }
  for (const h of c.hoses ?? []) {
    const due = deviceNextDue(h.lastService, h.nextService, intervalYears);
    if (due) dues.push(due);
  }
  if (!dues.length) return c.nextInspection;
  return dues.reduce((a, b) => (a < b ? a : b));
}

/** Most recent service across all devices — the customer's effective last
 * inspection. Falls back to the stored value when no device carries a date. */
export function deriveLastInspection(
  c: Pick<Customer, 'extinguishers' | 'hoses' | 'lastInspection'>
): string | undefined {
  const dates = [
    ...(c.extinguishers ?? []).map(e => e.lastInspection),
    ...(c.hoses ?? []).map(h => h.lastService),
  ].filter((d): d is string => !!d);
  if (!dates.length) return c.lastInspection;
  return dates.reduce((a, b) => (a > b ? a : b));
}
